import React, { useState, useEffect, useRef } from "react";
import Head from "next/head";
import { GetServerSideProps } from "next";
import { BRAND_NAMES } from "@/app/utils/utils";
import styles from "../styles/LPMoveBrasil.module.scss";
import ContactForm from "@/app/components/ContactForm/ContactFormMoveBrasil";
import { FooterMB } from "@/app/components/LPMoveBrasil/FooterMovBrasil";
import VeiculoMoveBrasil from "@/app/components/LPMoveBrasil/VeiculoMoveBrasil";

const BLOB_CONTAINER_URL =
	"https://sablobsitestoyotaprd.blob.core.windows.net/images";
const BLOB_CONTAINER_Desktop = "/Desktop/kyoto";
const BLOB_CONTAINER_Mobile = "/Mobile/kyoto";

interface MoveBrasilProps {
	imageUrlsDesktop: string[];
	imageUrlsMobile: string[];
}

const MoveBrasil: React.FC<MoveBrasilProps> = ({
	imageUrlsDesktop = [],
	imageUrlsMobile = [],
}) => {
	const [isMobile, setIsMobile] = useState(false);
	const [current, setCurrent] = useState(0);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const [openFaq, setOpenFaq] = useState<number | null>(null);

	const faqItems = [
		{
			q: "O programa pede entrada mínima?",
			a: "Não. O banco poderá financiar até 100% do valor do veículo, a depender da análise de crédito.",
		},
		{
			q: "É possível incluir agregados no financiamento?",
			a: "Não. O programa não permite a inclusão de agregados. São aceitos apenas seguros e proteção financeira.",
		},
		{
			q: "Se aprovado no GOV, o financiamento está garantido?",
			a: "Não. A aprovação no GOV válida apenas a elegibilidade do cliente ao programa. A aprovação do financiamento depende da análise de crédito da instituição financeira.",
		},
		{
			q: "Simulações recusadas podem ser aprovadas posteriormente?",
			a: "Sim. A recomendação é realizar uma nova simulação após a conclusão da integração via API. Há expectativa de maior flexibilidade nos critérios de análise de crédito ao longo do processo.",
		},
		{
			q: "O que é o MOVE Brasil?",
			a: "É um programa do Governo Federal que oferece condições especiais de financiamento para aquisição de veículos novos destinados a profissionais do transporte de passageiros, como motoristas de aplicativo e taxistas.",
		},
		{
			q: "Quem pode participar?",
			a: "Motoristas de aplicativo com cadastro ativo há pelo menos 12 meses na mesma plataforma e mínimo de 100 corridas no período; Taxistas com licença e registro ativo.",
		},
		{
			q: "Quais veículos podem ser financiados?",
			a: "Veículos novos, sustentáveis (flex, híbridos ou elétricos), habilitados no programa, com valor de até R$150 mil.",
		},
		{
			q: "Posso financiar veículo usado?",
			a: "Não. O programa contempla exclusivamente veículos zero quilômetro.",
		},
		{
			q: "Existe limite de valor do veículo?",
			a: "Sim. O valor máximo do automóvel é de R$150.000.",
		},
		{
			q: "Como faço minha inscrição?",
			a: "O cadastro é realizado na plataforma oficial do Governo Federal. Após a validação dos requisitos, o cliente poderá procurar uma concessionária e uma instituição financeira participante.",
		},
		{
			q: "O financiamento é aprovado automaticamente?",
			a: "Não. Mesmo estando elegível ao programa, o cliente passará por análise de crédito da instituição financeira.",
		},
		{
			q: "Quais são os principais benefícios para o cliente?",
			a: "Juros reduzidos; Condições facilitadas de financiamento; Possibilidade de renovação de frota; Acesso a veículos mais econômicos e sustentáveis.",
		},
		{
			q: "Quando o cliente pode procurar a concessionária?",
			a: "Após receber a confirmação de elegibilidade no portal do programa, o cliente já pode procurar uma concessionária participante para simulação e aquisição do veículo.",
		},
		{
			q: "Pessoas jurídicas (empresas) também podem comprar carro zero pelo programa?",
			a: "Não. O Move Brasil Táxi e Aplicativos é exclusivo para pessoa física, e limitado a um carro por CPF.",
		},
		{
			q: "Em quanto tempo terei retorno após cadastro?",
			a: "Em até 5 dias úteis após o cadastro na plataforma, o motorista receberá, via caixa postal do gov.br e via mensagem de WhatsApp, resposta sobre a análise de elegibilidade, ou seja, se ele pode ou não fazer parte do programa.",
		},
	];

	const handleFaqClick = (index: number) => {
		setOpenFaq((prev) => (prev === index ? null : index));
	};

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth < 780);
		check();
		window.addEventListener("resize", check);
		return () => window.removeEventListener("resize", check);
	}, []);

	const imageDesktop = imageUrlsDesktop;
	const imageMobile = imageUrlsMobile;
	const imageUrls = isMobile ? imageMobile : imageDesktop;

	const total = imageUrls.length;
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const goTo = (index: number) => {
		setCurrent((index + total) % total);
	};

	useEffect(() => {
		if (total === 0) return;
		timerRef.current = setInterval(() => {
			setCurrent((prev) => (prev + 1) % total);
		}, 4000);
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [total]);

	return (
		<>
			<Head>
				<title key="title">
					{BRAND_NAMES.META_TITLE} participa do programa Move Brasil
				</title>
				<meta
					name="description"
					content={`Compre seu carro com condições especiais no programa Move Brasil aqui na ${BRAND_NAMES.META_TITLE}`}
				/>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no"
				/>
			</Head>
			<div
				style={{ margin: 0, padding: 0, width: "100vw", color: "#000" }}
			>
				<div id="Carrossel" className={styles.carrossel}>
					<section
						style={{
							position: "relative",
							width: "100%",
						}}
					>
						{total === 0 ? (
							<p
								style={{
									textAlign: "center",
									marginTop: "2rem",
								}}
							>
								Nenhuma imagem encontrada.
							</p>
						) : (
							<>
								<div
									style={{
										width: "100%",
										position: "relative",
										display: "flex",
										justifyContent: "center",
									}}
								>
									{imageUrls.map((url, i) => (
										<img
											key={url}
											src={url}
											alt={`Banner Move Brasil ${i + 1}`}
											style={{
												display: "block",
												width: "min(100%, 1920px)",
												height: "auto",
												opacity: i === current ? 1 : 0,
												transition: "opacity 0.6s ease",
												visibility:
													i === current
														? "visible"
														: "hidden",
												position:
													i === current
														? "relative"
														: "absolute",
												top: 0,
												left: 0,
											}}
										/>
									))}
								</div>

								<button
									onClick={() => goTo(current - 1)}
									style={{
										position: "absolute",
										left: "calc(50% - 960px + 16px)",
										top: "50%",
										transform: "translateY(-50%)",
										zIndex: 10,
										background: "rgba(0,0,0,0.4)",
										color: "#fff",
										border: "none",
										borderRadius: "50%",
										width: 44,
										height: 44,
										fontSize: 20,
										cursor: "pointer",
									}}
								>
									‹
								</button>

								<button
									onClick={() => goTo(current + 1)}
									style={{
										position: "absolute",
										right: "calc(50% - 960px + 16px)",
										top: "50%",
										transform: "translateY(-50%)",
										zIndex: 10,
										background: "rgba(0,0,0,0.4)",
										color: "#fff",
										border: "none",
										borderRadius: "50%",
										width: 44,
										height: 44,
										fontSize: 20,
										cursor: "pointer",
									}}
								>
									›
								</button>

								<div
									style={{
										position: "absolute",
										bottom: 16,
										left: "50%",
										transform: "translateX(-50%)",
										display: "flex",
										gap: 8,
										zIndex: 10,
									}}
								>
									{imageUrls.map((_, i) => (
										<button
											key={i}
											onClick={() => goTo(i)}
											style={{
												width: 10,
												height: 10,
												borderRadius: "50%",
												border: "none",
												cursor: "pointer",
												background:
													i === current
														? "#fff"
														: "rgba(255,255,255,0.4)",
												padding: 0,
											}}
										/>
									))}
								</div>
							</>
						)}
					</section>
				</div>
				<div id="Intro" className={styles.intro}>
					<div className={styles["intro-img"]}></div>
					<div className={styles["intro-text"]}>
						<h1>
							É motorista de <span>aplicativo ou taxista</span>?
						</h1>
						<p>
							O <span>Move Brasil</span> Táxi e Aplicativos é uma
							linha de crédito do Governo Federal para motoristas
							de aplicativo e taxistas que desejam modernizar sua
							frota.
						</p>
						<p>
							O <span>Yaris Cross XR</span> faz parte do programa.
							Tenha mais eficiência, conforto e segurança na sua
							rotina, com condições diferenciadas de
							financiamento.
						</p>
						<button className={styles.bluebtn}>
							GARANTA O SEU
						</button>
					</div>
				</div>
				<div id="About" className={styles.about}>
					<h2>
						Quem pode participar do <span>Move Brasil</span>?
					</h2>
					<p>
						O Programa Move Brasil possui critérios* específicos
						para participação.
					</p>
					<div className={styles["about-card"]}>
						<div className={styles.motAplicativo}>
							<div>
								<div
									className={`${styles["about-content"]} ${styles["about-content--app"]}`}
								>
									<h3>Motoristas de Aplicativo</h3>
									<ul>
										<li>
											Possuir cadastro ativo há pelo menos
											12 meses na mesma plataforma.
										</li>
										<li>
											Ter realizado, no mínimo, 100
											corridas nesse período.
										</li>
										<li>
											Atender aos critérios de validação
											definidos pelo programa.
										</li>
									</ul>
								</div>
							</div>
							<img
								src="https://sablobsitestoyotadev.blob.core.windows.net/images/move-brasil/Yaris_Cross_03.webp"
								alt="Yaris Cross azul"
							/>
						</div>
					</div>
					<div className={styles["about-card"]}>
						<div className={styles.motTaxi}>
							<div className={styles["taxi-img"]}>
								<img
									src="https://sablobsitestoyotadev.blob.core.windows.net/images/move-brasil/Yaris_Cross_01.webp"
									alt="Yaris-cross Azul"
								/>
							</div>
							<div className={styles["about-cardt"]}>
								<div
									className={`${styles["about-content"]} ${styles["about-content--taxi"]}`}
								>
									<h3>Taxistas</h3>
									<span
										className={`${styles.point} ${styles.line}`}
									></span>
									<ul>
										<li>
											Titulares de autorização, permissão
											ou concessão do Poder Público.
										</li>
										<li>
											Ter licenças e registros
											ativos/regularizados.
										</li>
										<li>
											Cooperativas de Taxi com convênio
											ativo com bancos parceiros Move
											Brasil.
										</li>
										<li>
											Atender às exigências de
											documentação e regularidade fiscal.
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<p>
						*O programa é exclusivo para pessoas físicas (CPF) e
						permite a compra de um veículo por beneficiário.
					</p>
				</div>
				<div id="options" className={styles.options}>
					<h2>As melhores condições do mercado</h2>
					<div className={styles["options-content"]}>
						<div className={styles["option-card"]}>
							<div className={styles["option-card-img"]}>
								<img
									src="/images/Icon_HandMoney.svg"
									alt="Juros Reduzidos"
								/>
							</div>
							<div className="option-card-text">
								<h3>Juros Reduzidos</h3>
								<p>
									Taxas especiais via BNDES: até 0,91% a.m.
									para mulheres e 0,99% a.m. para homens.
								</p>
							</div>
						</div>
						<div className={styles["option-card"]}>
							<div className={styles["option-card-img"]}>
								<img
									src="/images/Icon_HandCoin.svg"
									alt="Sem entrada mínima"
								/>
							</div>
							<div className="option-card-text">
								<h3>Sem entrada mínima</h3>
								<p>
									Financiamento de até 100% do valor do
									veículo. Carência de 6 meses e prazo de até
									72x.
								</p>
							</div>
						</div>
						<div className={styles["option-card"]}>
							<div className={styles["option-card-img"]}>
								<img
									src="/images/Icon_group.svg"
									alt="Frota sustentável"
								/>
							</div>
							<div className="option-card-text">
								<h3>Frota sustentável</h3>
								<p>
									Veículos zero km flex, híbridos ou elétricos
									até R$ 150 mil. IOF isento.
								</p>
							</div>
						</div>
						<div className={styles["option-card"]}>
							<div className={styles["option-card-img"]}>
								<img
									src="/images/Icon_car.svg"
									alt="Renovação de frota"
								/>
							</div>
							<div className="option-card-text">
								<h3>Renovação de frota</h3>
								<p>
									Fundo de R$ 30 bilhões do Governo Federal
									para motoristas profissionais renovarem o
									carro.
								</p>
							</div>
						</div>
					</div>
				</div>
				<div id="Model" className={styles["model-section"]}>
					<div className={styles.model}>
						<div className={styles["model-img"]}>
							<h2>Conheça o Yaris Cross XR</h2>
							<VeiculoMoveBrasil />
						</div>
						<div className={styles["model-characteristics"]}>
							<div className={styles["characteristics-card"]}>
								<h3>MOTOR</h3>
								<p>
									1.5L Dual VVT-i 16V DOHC² Flex para economia
									e respostas rápidas{" "}
								</p>
							</div>
							<div className={styles["characteristics-card"]}>
								<h3>CAPACIDADE</h3>
								<p>
									Acomoda até 5 ocupantes com conforto e amplo
									espaço interno{" "}
								</p>
							</div>
							<div className={styles["characteristics-card"]}>
								<h3>TRANSMISSÃO</h3>
								<p>
									Câmbio CVT Multidrive com 7 marchas
									simuladas e paddle shift para condução
									suave.{" "}
								</p>
							</div>
							<div className={styles["characteristics-card"]}>
								<h3>TIPO DE COMBUSTÍVEL</h3>
								<p>Etanol/gasolina</p>
							</div>
							<div className={styles["characteristics-card"]}>
								<h3>DESEMPENHO</h3>
								<p>
									Potência de 122 cv e 15,3 kgf.m de torque
									para condução eficiente e segura.{" "}
								</p>
							</div>
						</div>
					</div>
					<button className={styles.bluebtn}>
						QUERO O MEU YARIS CROSS XR
					</button>
				</div>
				<div id="HowWorks" className={styles.howWorks}>
					<div className={styles["howWorks-text"]}>
						<h2>
							Como funciona - <span>Passo a passo</span>
						</h2>
					</div>
					<div className={styles["howWorks-steps"]}>
						<div className={styles.step}>
							<h3>Cadastro e consentimento</h3>
							<p>
								Cadastro na plataforma <span>gov.br.</span>{" "}
								Motorista autoriza avaliação dos seus dados.
							</p>
						</div>
						<div className={styles.step}>
							<h3>Confirmação</h3>
							<p>
								Em até <span>5 dias úteis</span> por WhatsApp e
								caixa portal com elegibilidade ou não.
							</p>
						</div>
						<div className={styles.step}>
							<h3>Solicitação de financiamento</h3>
							<p>
								Com confirmação em mãos, busque o banco{" "}
								<span>para análise.</span>
							</p>
						</div>
					</div>
				</div>
				<div id="FAQ" className={styles.faq}>
					<h2>Perguntas Frequentes</h2>
					{faqItems.map((item, index) => {
						const isOpen = openFaq === index;
						return (
							<div
								key={index}
								className={styles["faq-item"]}
								onClick={() => handleFaqClick(index)}
							>
								<div className={styles["faq-header"]}>
									<h4>{item.q}</h4>
									<button
										className={styles["faq-toggle"]}
										aria-expanded={isOpen}
									>
										{isOpen ? "−" : "+"}
									</button>
								</div>
								{isOpen && (
									<p className={styles["faq-answer"]}>
										{item.a}
									</p>
								)}
							</div>
						);
					})}
				</div>
				<div id="Contact" className={styles.contact}>
					<div className={styles["contact-texts"]}>
						<h2>
							Não deixe essa oportunidade passar. Faça sua
							simulação.
						</h2>
						<p>
							Confirme sua elegibilidade ao Programa Move Brasil e
							descubra quanto você pode economizar no Toyota Yaris
							Cross XR. Converse com um especialista da{" "}
							{BRAND_NAMES.META_TITLE}.
						</p>
					</div>
					<ContactForm />
				</div>
			</div>
			<FooterMB />
		</>
	);
};

async function fetchBlobImages(prefix: string): Promise<string[]> {
	try {
		const listUrl = `${BLOB_CONTAINER_URL}?restype=container&comp=list&prefix=${prefix}`;
		const response = await fetch(listUrl);
		if (!response.ok) return [];
		const xml = await response.text();
		const matches = [...xml.matchAll(/<Url>([^<]+)<\/Url>/g)];
		return matches
			.map((m) => m[1])
			.filter((url) => /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url));
	} catch {
		return [];
	}
}

export const getServerSideProps: GetServerSideProps = async () => {
	const [imageUrlsDesktop, imageUrlsMobile] = await Promise.all([
		fetchBlobImages(`move-brasil/Banners${BLOB_CONTAINER_Desktop}`),
		fetchBlobImages(`move-brasil/Banners${BLOB_CONTAINER_Mobile}`),
	]);

	return { props: { imageUrlsDesktop, imageUrlsMobile } };
};

export default MoveBrasil;
